export const COACH_PANEL_MIGRATION_EVENT = 'coach_panel_migration';

const SURFACES = [
  'notes',
  'member_notes',
  'program',
  'summary',
  'sessions',
  'activities',
  'insights',
  'assessments',
  'messages',
  'goals',
  'connect',
  'qualifications',
  'profile',
  'account',
  'calendar',
  'payrates',
  'documents',
  'member_preview',
  'home',
  // The availability calendar. 'calendar' above is Calendar settings.
  'coach_calendar',
  'inbox',
  'conversation',
  'resources',
] as const;
const REASONS = [
  'enabled',
  'flag_off',
  'legacy_override',
  'storage_unavailable',
  'render_error',
  'load_error',
  'invalid_context',
  'user_requested',
  'not_coach',
  'tab_unavailable',
  'no_assignment',
  'assignment_mismatch',
] as const;
const KINDS = ['decision', 'fallback', 'error', 'ineligible'] as const;
export type MigrationSurface = (typeof SURFACES)[number];
export type MigrationReason = (typeof REASONS)[number];
export type MigrationKind = (typeof KINDS)[number];

export function migrationProperties(input: Record<string, unknown>) {
  const surface = SURFACES.find((value) => value === input.surface);
  const reason = REASONS.find((value) => value === input.reason);
  const kind = KINDS.find((value) => value === input.kind);
  if (!surface || !reason || !kind) return null;
  if (
    kind === 'decision' &&
    ![
      'enabled',
      'flag_off',
      'legacy_override',
      'storage_unavailable',
      'invalid_context',
    ].includes(reason)
  )
    return null;
  if (
    kind === 'error' &&
    ![
      'render_error',
      'load_error',
      'storage_unavailable',
      'invalid_context',
    ].includes(reason)
  )
    return null;
  if (
    kind === 'fallback' &&
    ![
      'flag_off',
      'legacy_override',
      'storage_unavailable',
      'user_requested',
    ].includes(reason)
  )
    return null;
  if (
    kind === 'ineligible' &&
    ![
      'not_coach',
      'tab_unavailable',
      'no_assignment',
      'assignment_mismatch',
    ].includes(reason)
  )
    return null;
  return {
    schema_version: 1,
    surface,
    kind,
    reason,
    owner: 'pod-itc',
    bu_system: 'coach_panel',
  };
}

export function createMigrationEmitter(
  send: (name: string, properties: Record<string, unknown>) => void
) {
  let remaining = 100;
  return (
    surface: MigrationSurface,
    kind: MigrationKind,
    reason: MigrationReason
  ) => {
    const properties = migrationProperties({ surface, kind, reason });
    if (!properties || remaining === 0) return;
    remaining -= 1;
    try {
      send(COACH_PANEL_MIGRATION_EVENT, properties);
    } catch {
      // Observability cannot prevent rendering or rollback navigation.
    }
  };
}

interface MigrationRumEvent {
  type: string;
  action?: { id?: unknown; target?: { name?: string } };
  context?: Record<string, unknown>;
  view: { url: string; referrer?: string; name?: string };
}

export function redactMigrationEvent(event: MigrationRumEvent): boolean {
  if (
    event.type !== 'action' ||
    event.action?.target?.name !== COACH_PANEL_MIGRATION_EVENT
  )
    return true;
  const properties = migrationProperties(event.context ?? {});
  if (!properties) return false;
  event.context = properties;
  event.view.url = `/platform/coach/${properties.surface}`;
  event.view.referrer = '';
  event.view.name = 'Coach Panel';
  return true;
}
